import { logger } from '@/lib/logger';
import { ERROR_HANDLING, ERROR_HANDLING_TOAST } from '@/lib/constants';
import { normalizeError, HttpError } from '@/lib/error-types';
import { toast } from 'sonner';

/**
 * Toast deduplication tracking
 * Prevents repeated toasts for the same error within a time window
 */
const recentToasts = new Map<string, number>();

function getToastKey(error: unknown): string {
  if (error instanceof Error) {
    // Use name + first 100 chars of message as key
    return `${error.name}:${error.message.slice(0, 100)}`;
  }
  return String(error).slice(0, 100);
}

function shouldShowToast(error: unknown): boolean {
  const key = getToastKey(error);
  const now = Date.now();
  const lastShown = recentToasts.get(key);
  
  // If same error shown within dedup window, skip
  if (lastShown && now - lastShown < ERROR_HANDLING_TOAST.DEDUP_WINDOW_MS) {
    logger.debug('[GlobalErrorHandler] Toast deduplicated', { key });
    return false;
  }
  
  recentToasts.set(key, now);
  
  // Cleanup old entries periodically (max 50 entries)
  if (recentToasts.size > 50) {
    for (const [k, v] of recentToasts) {
      if (now - v > ERROR_HANDLING_TOAST.DEDUP_WINDOW_MS) {
        recentToasts.delete(k);
      }
    }
  }
  
  return true;
}

/**
 * Generate unique correlation ID for error tracking
 */
export function generateErrorId(): string {
  return `${ERROR_HANDLING.ERROR_ID_PREFIX}${Date.now()}_${Math.random()
    .toString(36)
    .substr(2, ERROR_HANDLING.CORRELATION_ID_LENGTH)}`;
}

/**
 * Detect chunk loading errors
 */
export function isChunkLoadError(error: Error | string): boolean {
  const errorMessage = typeof error === 'string' ? error : error?.message ?? '';
  const errorName = typeof error === 'object' ? error?.name ?? '' : '';
  
  return ERROR_HANDLING.CHUNK_ERROR_PATTERNS.some(
    pattern => errorMessage.includes(pattern) || errorName.includes(pattern)
  );
}

/**
 * Categorize error for logging
 */
export function categorizeError(
  error: Error | string,
  source: 'window' | 'unhandledrejection' | 'resource'
): typeof ERROR_HANDLING.CATEGORIES[keyof typeof ERROR_HANDLING.CATEGORIES] {
  if (source === 'unhandledrejection') {
    return ERROR_HANDLING.CATEGORIES.PROMISE_REJECTION;
  }
  
  if (source === 'resource') {
    return ERROR_HANDLING.CATEGORIES.RESOURCE_LOAD;
  }
  
  if (isChunkLoadError(error)) {
    return ERROR_HANDLING.CATEGORIES.CHUNK;
  }
  
  if (error instanceof Error) {
    return ERROR_HANDLING.CATEGORIES.RUNTIME;
  }
  
  return ERROR_HANDLING.CATEGORIES.UNKNOWN;
}

/**
 * Handle global uncaught errors
 */
export function handleGlobalError(event: ErrorEvent): void {
  const errorId = generateErrorId();
  const category = categorizeError(event.error, 'window');
  
  // Normalize to universal error contract
  const normalizedError = normalizeError(event.error);
  
  // Enhanced structured logging
  logger.error('[Global] Uncaught error:', {
    errorId,
    category,
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    error: normalizedError,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
  });

  // Report to Sentry (non-chunk errors; chunk errors are handled with auto-refresh)
  if (!isChunkLoadError(event.error)) {
    import('@/lib/sentry').then(({ Sentry }) => {
      Sentry.captureException(event.error ?? event.message, {
        tags: { category, errorId },
      });
    });
  }
  
  // User feedback for chunk errors
  if (isChunkLoadError(event.error)) {
    const isModulePathError = 
      event.error.message?.includes('.tsx') || 
      event.error.message?.includes('.ts') ||
      event.error.message?.includes('/src/pages/') ||
      event.error.message?.includes('/src/components/');
    
    const message = isModulePathError
      ? 'App deployment is updating. Refreshing now...'
      : 'Application update detected. Please refresh the page.';
    
    toast.error(message, {
      duration: ERROR_HANDLING.TOAST_DURATION_CRITICAL,
      action: isModulePathError ? undefined : {
        label: 'Refresh Now',
        onClick: () => window.location.reload(),
      },
    });
    
    // Auto-refresh for deployment mismatches
    if (isModulePathError) {
      setTimeout(() => window.location.reload(), 2000);
    }
    
    // Prevent default to avoid console spam
    event.preventDefault();
  }
}

/**
 * Handle unhandled promise rejections
 */
export function handleUnhandledRejection(event: PromiseRejectionEvent): void {
  const errorId = generateErrorId();
  const category = categorizeError(event.reason, 'unhandledrejection');
  
  // Normalize to universal error contract
  const normalizedError = normalizeError(event.reason);
  
  // Enhanced structured logging
  logger.error('[Global] Unhandled promise rejection:', {
    errorId,
    category,
    reason: normalizedError,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
  });
  
  // Prevent default console spam
  event.preventDefault();
  
  // User-friendly toast notification
  if (isChunkLoadError(event.reason)) {
    const isModulePathError = 
      event.reason?.message?.includes('.tsx') || 
      event.reason?.message?.includes('.ts') ||
      event.reason?.message?.includes('/src/pages/') ||
      event.reason?.message?.includes('/src/components/');
    
    const message = isModulePathError
      ? 'App deployment is updating. Refreshing now...'
      : 'Application update detected. Please refresh the page.';
    
    toast.error(message, {
      duration: ERROR_HANDLING.TOAST_DURATION_CRITICAL,
      action: isModulePathError ? undefined : {
        label: 'Refresh Now',
        onClick: () => window.location.reload(),
      },
    });
    
    // Auto-refresh for deployment mismatches
    if (isModulePathError) {
      setTimeout(() => window.location.reload(), 2000);
    }
  } else {
    // Skip HttpError instances — already handled by TanStack Query → ErrorBoundary
    if (event.reason instanceof HttpError) {
      return;
    }

    // Skip benign browser/network rejections that don't need user-facing toasts
    const reason = event.reason;
    const isAbort = reason?.name === 'AbortError';
    const isCancelledError = 
      reason?.name === 'CancelledError' || 
      (reason instanceof Error && reason.message === 'cancelled');
    const isNetworkTransient = reason instanceof TypeError &&
      (reason.message?.includes('Failed to fetch') || reason.message?.includes('Load failed'));
    const isSupabaseRealtime = typeof reason?.message === 'string' &&
      (reason.message.includes('WebSocket') || reason.message.includes('realtime'));
    if (isAbort || isCancelledError || isNetworkTransient || isSupabaseRealtime) {
      return;
    }

    // Report non-benign rejections to Sentry
    import('@/lib/sentry').then(({ Sentry }) => {
      Sentry.captureException(event.reason, {
        tags: { category, errorId },
      });
    });

    // Generic error toast for other promise rejections - WITH DEDUPLICATION
    if (shouldShowToast(event.reason)) {
      toast.error('An unexpected error occurred. If this persists, please refresh the page.', {
        duration: ERROR_HANDLING.TOAST_DURATION_STANDARD,
      });
    }
  }
}

/**
 * Initialize global error handlers
 */
export function initGlobalErrorHandlers(): () => void {
  // Add global error handler
  window.addEventListener('error', handleGlobalError);
  
  // Add unhandled rejection handler
  window.addEventListener('unhandledrejection', handleUnhandledRejection);
  
  // Return cleanup function
  return () => {
    window.removeEventListener('error', handleGlobalError);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  };
}
